import { useEffect, useMemo, useState } from 'react'
import { AlertTriangle, ArrowLeft, CheckCircle2, Loader2, Search, UserCog, UserMinus, UserPlus, X } from 'lucide-react'
import { Button } from '@/components/ui'
import {
  addPersonToPartnerGroup,
  checkPartnerPersonExists,
  endPartnerGroupMembership,
  fetchPartnerGroupCandidates,
  fetchPartnerGroupMembership,
  type PartnerGroupMembershipMode,
  type PartnerGroupMembershipRow,
  type PartnerPersonExistsRow,
} from '@/services/partnerGroupMembershipService'
import {
  applyDisplayNameChange,
  fetchPartyIdentityPreview,
  setPersonAppUser,
  type PartyIdentityPreview,
} from '@/services/partyIdentityService'
import type { PartnerStatusCandidate } from '@/services/partnerStatusService'

export type { PartnerGroupMembershipMode }

const MODE_LABELS: Record<PartnerGroupMembershipMode, string> = {
  add_person: 'Добавление персоны в группу франчайзи',
  end_person: 'Выход персоны из группы',
  display_name: 'Форма имени в списках',
}

const INPUT_CLASS =
  'mt-1 w-full rounded border border-border bg-background px-2 py-2 text-sm text-foreground outline-none focus:border-primary'

function errorMessage(error: unknown, fallback: string): string {
  if (error instanceof Error && error.message) return error.message
  if (typeof error === 'object' && error !== null && 'message' in error) {
    const message = String((error as { message?: unknown }).message ?? '').trim()
    if (message) return message
  }
  return fallback
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10)
}

function timestampAtNoon(date: string): string | null {
  return date ? `${date} 12:00:00` : null
}

function matchesQuery(haystack: Array<string | number | null | undefined>, query: string): boolean {
  const trimmed = query.trim().toLowerCase()
  if (!trimmed) return true
  return haystack
    .filter(value => value !== null && value !== undefined && value !== '')
    .join(' ')
    .toLowerCase()
    .includes(trimmed)
}

function formatDate(value: string | null | undefined): string {
  if (!value) return '—'
  const date = new Date(value.length <= 10 ? `${value}T00:00:00` : value.replace(' ', 'T'))
  if (Number.isNaN(date.getTime())) return value
  return date.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

function partnerLabel(partner: PartnerStatusCandidate): string {
  const fio = partner.persons_fio.trim() || 'без персон'
  return `№${partner.partner_number} · ${fio}`
}

function memberLabel(member: PartnerGroupMembershipRow): string {
  return `${member.last_name} ${member.first_name}`.trim() || `person_id ${member.person_id}`
}

export function PartnerGroupMembershipModal({
  initialMode,
  onBack,
  onClose,
  onDone,
}: {
  initialMode: PartnerGroupMembershipMode
  onBack?: () => void
  onClose: () => void
  onDone?: () => void
}) {
  const mode = initialMode
  const [partners, setPartners] = useState<PartnerStatusCandidate[]>([])
  const [members, setMembers] = useState<PartnerGroupMembershipRow[]>([])
  const [personMatches, setPersonMatches] = useState<PartnerPersonExistsRow[]>([])
  const [loading, setLoading] = useState(true)
  const [membersLoading, setMembersLoading] = useState(false)
  const [personCheckLoading, setPersonCheckLoading] = useState(false)
  const [partnerQuery, setPartnerQuery] = useState('')
  const [partnerNumber, setPartnerNumber] = useState<number | null>(null)
  const [selectedPersonId, setSelectedPersonId] = useState<number | null>(null)
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [identityPreview, setIdentityPreview] = useState<PartyIdentityPreview | null>(null)
  const [identityLoading, setIdentityLoading] = useState(false)
  const [appUserId, setAppUserId] = useState<string | null>(null)
  const [appFirstName, setAppFirstName] = useState('')
  const [appLastName, setAppLastName] = useState('')
  const [rememberAppUser, setRememberAppUser] = useState(true)
  const [gender, setGender] = useState<'м' | 'ж'>('ж')
  const [tgUsername, setTgUsername] = useState('')
  const [eventDate, setEventDate] = useState(todayIso())
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  async function loadPartners() {
    setLoading(true)
    try {
      setPartners(await fetchPartnerGroupCandidates())
      setError(null)
    } catch (loadError: unknown) {
      setError(errorMessage(loadError, 'Не удалось загрузить франчайзи'))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void loadPartners()
  }, [])

  const filteredPartners = useMemo(() => {
    return partners
      .filter(row => matchesQuery([
        row.partner_number,
        row.persons_fio,
        row.current_status,
        row.cities_text,
      ], partnerQuery))
      .slice(0, 80)
  }, [partnerQuery, partners])

  const selectedPartner = useMemo(
    () => partners.find(row => row.partner_number === partnerNumber) ?? null,
    [partnerNumber, partners]
  )

  const activeMembers = useMemo(
    () => members.filter(member => member.membership_status === 'АКТИВЕН' && !member.end_date),
    [members]
  )

  const selectedMember = useMemo(
    () => activeMembers.find(member => member.person_id === selectedPersonId) ?? null,
    [activeMembers, selectedPersonId]
  )

  // Радиус формы имени: текущее значение, кандидаты-аккаунты и сколько строк
  // рейтинга встанет в очередь пересчёта.
  useEffect(() => {
    if (mode !== 'display_name' || selectedPersonId === null) {
      setIdentityPreview(null)
      return
    }
    let cancelled = false
    setIdentityLoading(true)
    fetchPartyIdentityPreview(selectedPersonId)
      .then(data => {
        if (cancelled) return
        setIdentityPreview(data)
        setDisplayName(data.person.display_name ?? '')
        const exact = data.app_users.find(user => user.match_kind === 'exact') ?? data.app_users[0] ?? null
        setAppUserId(exact?.user_id ?? null)
        setAppFirstName(exact?.first_name ?? data.person.first_name)
        setAppLastName(exact?.last_name ?? data.person.last_name)
        setError(null)
      })
      .catch(previewError => {
        if (!cancelled) setError(errorMessage(previewError, 'Не удалось собрать радиус изменения'))
      })
      .finally(() => {
        if (!cancelled) setIdentityLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [mode, selectedPersonId])

  useEffect(() => {
    if (loading) return
    if (partnerNumber !== null && filteredPartners.some(row => row.partner_number === partnerNumber)) return
    setPartnerNumber(filteredPartners.length === 1 ? filteredPartners[0].partner_number : null)
  }, [filteredPartners, loading, partnerNumber])

  useEffect(() => {
    let cancelled = false

    if (partnerNumber === null) {
      setMembers([])
      setSelectedPersonId(null)
      return
    }

    setMembersLoading(true)
    fetchPartnerGroupMembership(partnerNumber)
      .then(rows => {
        if (cancelled) return
        setMembers(rows)
        setSelectedPersonId(rows.filter(row => row.membership_status === 'АКТИВЕН' && !row.end_date).length === 1
          ? rows.find(row => row.membership_status === 'АКТИВЕН' && !row.end_date)?.person_id ?? null
          : null)
      })
      .catch(loadError => {
        if (!cancelled) setError(errorMessage(loadError, 'Не удалось загрузить состав группы'))
      })
      .finally(() => {
        if (!cancelled) setMembersLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [partnerNumber])

  useEffect(() => {
    let cancelled = false
    const first = firstName.trim()
    const last = lastName.trim()

    if (mode !== 'add_person' || !first || !last) {
      setPersonMatches([])
      return
    }

    setPersonCheckLoading(true)
    checkPartnerPersonExists({ firstName: first, lastName: last })
      .then(rows => {
        if (!cancelled) setPersonMatches(rows)
      })
      .catch(() => {
        if (!cancelled) setPersonMatches([])
      })
      .finally(() => {
        if (!cancelled) setPersonCheckLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [firstName, lastName, mode])

  const startDate = timestampAtNoon(eventDate)
  const canSubmit = !saving
    && !loading
    && partnerNumber !== null
    && (mode === 'add_person'
      ? Boolean(eventDate && firstName.trim() && lastName.trim() && gender && startDate)
      : mode === 'end_person'
        ? Boolean(eventDate && selectedMember !== null)
        // Форма имени: пустое значение — законная операция «вернуть паспортное ФИО».
        : Boolean(selectedMember !== null && !identityLoading))

  async function reloadMembership(partner: number) {
    setMembers(await fetchPartnerGroupMembership(partner))
    await loadPartners()
  }

  async function handleSubmit() {
    if (!canSubmit || partnerNumber === null) return

    setSaving(true)
    setError(null)
    setSuccess(null)

    try {
      if (mode === 'add_person') {
        if (!startDate) return
        const result = await addPersonToPartnerGroup({
          partnerNumber,
          firstName,
          lastName,
          gender,
          tgUsername,
          startDate,
        })
        setSuccess(
          `Персона ${result.last_name} ${result.first_name} добавлена в группу франчайзи №${result.partner_number}.`
        )
        setFirstName('')
        setLastName('')
        setTgUsername('')
      } else if (selectedMember) {
        if (mode === 'end_person') {
          const result = await endPartnerGroupMembership({
            partnerNumber,
            firstName: selectedMember.first_name,
            lastName: selectedMember.last_name,
            endDate: eventDate,
          })
          // Не нашла участника — RPC отказывает ошибкой (20261013190700), сюда не доходит.
          setSuccess(
            `Участие ${result.last_name} ${result.first_name} в группе франчайзи №${result.partner_number} закрыто.`
          )
        } else {
          // Сначала связь: дальше имя аккаунта будет синхронизироваться само, и
          // искать его по совпадению ФИО больше не придётся.
          if (appUserId && rememberAppUser) {
            await setPersonAppUser({
              personId: selectedMember.person_id,
              userId: appUserId,
              syncName: false,
              note: 'Связано при задании формы имени',
            })
          }
          const result = await applyDisplayNameChange({
            personId: selectedMember.person_id,
            displayName,
            userId: appUserId,
            userFirstName: appUserId ? appFirstName : null,
            userLastName: appUserId ? appLastName : null,
          })
          setSuccess(
            (result.new_display_name
              ? `В списках эта персона теперь показывается как «${result.new_display_name}».`
              : 'Форма имени снята — в списках снова показывается паспортное ФИО.') +
              ` Строк ШМП обновлено: ${result.shmp_rows_updated}.` +
              ` Строк рейтингов обновлено: ${result.rating_rows_updated}.` +
              (result.app_user_id ? ' Имя аккаунта приложения обновлено.' : '')
          )
        }
      }

      await reloadMembership(partnerNumber)
      onDone?.()
    } catch (submitError: unknown) {
      setError(errorMessage(submitError, 'Операция не выполнена'))
    } finally {
      setSaving(false)
    }
  }

  const Icon = mode === 'add_person' ? UserPlus : mode === 'display_name' ? UserCog : UserMinus

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 px-4 py-8" onClick={onClose}>
      <section
        className="my-6 w-full max-w-3xl rounded-xl bg-background p-4 shadow-lg"
        onClick={event => event.stopPropagation()}
      >
        <div className="mb-3 flex items-start justify-between gap-3">
          <div className="flex min-w-0 items-start gap-2">
            {onBack && (
              <button
                type="button"
                onClick={onBack}
                className="mt-0.5 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground"
                title="Вернуться к выбору кейса"
                aria-label="Вернуться к выбору кейса"
              >
                <ArrowLeft size={16} />
              </button>
            )}
            <div className="min-w-0">
              <div className="flex items-center gap-1.5 text-sm font-semibold text-foreground">
                <Icon size={15} />
                {MODE_LABELS[mode]}
              </div>
              <div className="mt-0.5 text-xs text-muted-foreground">
                Выберите франчайзи и заполните параметры изменения.
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground"
            aria-label="Закрыть"
          >
            <X size={16} />
          </button>
        </div>

        {error && (
          <div className="mb-3 flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
            <AlertTriangle size={18} className="mt-0.5 shrink-0" />
            <div>{error}</div>
          </div>
        )}

        {success && (
          <div className="mb-3 flex items-start gap-2 rounded-md border border-green-200 bg-green-50 p-3 text-sm text-green-700">
            <CheckCircle2 size={18} className="mt-0.5 shrink-0" />
            <div>{success}</div>
          </div>
        )}

        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <div className="min-w-0">
            <label className="block text-xs font-medium text-muted-foreground">
              Франчайзи
              <div className="mt-1 flex h-9 items-center gap-2 rounded-md border border-border bg-background px-2 focus-within:border-primary">
                <Search size={15} className="text-muted-foreground" />
                <input
                  value={partnerQuery}
                  onChange={event => setPartnerQuery(event.target.value)}
                  className="min-w-0 flex-1 bg-transparent text-sm text-foreground outline-none placeholder:text-muted-foreground"
                  placeholder="номер, ФИО, город, статус"
                />
              </div>
            </label>

            {loading ? (
              <div className="mt-1 flex h-[154px] items-center justify-center gap-2 rounded border border-border bg-muted/30 text-sm text-muted-foreground">
                <Loader2 size={16} className="animate-spin" />
                Загрузка
              </div>
            ) : (
              <select
                value={partnerNumber ?? ''}
                onChange={event => {
                  const value = event.target.value
                  setPartnerNumber(value ? Number(value) : null)
                  setSuccess(null)
                  setError(null)
                }}
                size={6}
                className="mt-1 w-full rounded border border-border bg-background px-2 py-1 text-sm text-foreground outline-none focus:border-primary"
              >
                {filteredPartners.map(partner => (
                  <option key={partner.partner_number} value={partner.partner_number}>
                    {partnerLabel(partner)}
                    {partner.current_status ? ` · ${partner.current_status}` : ''}
                    {partner.cities_text ? ` · ${partner.cities_text}` : ''}
                  </option>
                ))}
              </select>
            )}
            <span className="mt-1 block text-[11px] font-normal text-muted-foreground">
              Показано {filteredPartners.length} из {partners.length}
            </span>

            {selectedPartner && (
              <div className="mt-3 rounded-md border border-border bg-background p-3 text-xs text-muted-foreground">
                <div className="text-sm font-semibold text-foreground">{partnerLabel(selectedPartner)}</div>
                <div className="mt-1">Статус: {selectedPartner.current_status ?? '—'}</div>
                <div className="mt-1">Активные студии: {selectedPartner.active_studio_count}</div>
              </div>
            )}
          </div>

          <div className="min-w-0 space-y-3">
            {mode !== 'display_name' && (
              <label className="block text-xs font-medium text-muted-foreground">
                Дата изменения
                <input
                  type="date"
                  value={eventDate}
                  onChange={event => setEventDate(event.target.value)}
                  className={INPUT_CLASS}
                />
              </label>
            )}

            {mode === 'add_person' ? (
              <>
                <div className="grid grid-cols-2 gap-2">
                  <label className="block text-xs font-medium text-muted-foreground">
                    Имя
                    <input value={firstName} onChange={event => setFirstName(event.target.value)} className={INPUT_CLASS} />
                  </label>
                  <label className="block text-xs font-medium text-muted-foreground">
                    Фамилия
                    <input value={lastName} onChange={event => setLastName(event.target.value)} className={INPUT_CLASS} />
                  </label>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <label className="block text-xs font-medium text-muted-foreground">
                    Пол
                    <select value={gender} onChange={event => setGender(event.target.value as 'м' | 'ж')} className={INPUT_CLASS}>
                      <option value="ж">ж</option>
                      <option value="м">м</option>
                    </select>
                  </label>
                  <label className="block text-xs font-medium text-muted-foreground">
                    Telegram
                    <input
                      value={tgUsername}
                      onChange={event => setTgUsername(event.target.value)}
                      className={INPUT_CLASS}
                      placeholder="без @, необязательно"
                    />
                  </label>
                </div>
                {(personCheckLoading || personMatches.length > 0) && (
                  <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
                    {personCheckLoading ? (
                      <span className="inline-flex items-center gap-2"><Loader2 size={13} className="animate-spin" />Проверка персоны</span>
                    ) : (
                      <>
                        <div className="font-medium">Найдены совпадения по имени и фамилии:</div>
                        <div className="mt-1 space-y-0.5">
                          {personMatches.slice(0, 4).map(row => (
                            <div key={`${row.person_id}-${row.partner_number ?? 'none'}`}>
                              person_id {row.person_id}
                              {row.partner_number ? ` · франчайзи №${row.partner_number}` : ' · без группы'}
                            </div>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                )}
              </>
            ) : (
              <div>
                <label className="block text-xs font-medium text-muted-foreground">
                  {mode === 'display_name' ? 'Персона' : 'Активный участник'}
                  {membersLoading ? (
                    <div className="mt-1 flex h-24 items-center justify-center gap-2 rounded border border-border bg-muted/30 text-sm text-muted-foreground">
                      <Loader2 size={16} className="animate-spin" />
                      Загрузка
                    </div>
                  ) : (
                    <select
                      value={selectedPersonId ?? ''}
                      onChange={event => setSelectedPersonId(event.target.value ? Number(event.target.value) : null)}
                      size={5}
                      className="mt-1 w-full rounded border border-border bg-background px-2 py-1 text-sm text-foreground outline-none focus:border-primary"
                    >
                      {activeMembers.map(member => (
                        <option key={`${member.person_id}-${member.start_date}`} value={member.person_id}>
                          {memberLabel(member)} · с {formatDate(member.start_date)}
                        </option>
                      ))}
                    </select>
                  )}
                </label>
                <span className="mt-1 block text-[11px] text-muted-foreground">
                  Активных участников: {activeMembers.length}
                </span>
                {mode === 'display_name' && selectedMember && (
                  <div className="mt-3 space-y-3">
                    <label className="block text-xs font-medium text-muted-foreground">
                      Как показывать в списках
                      <input
                        value={displayName}
                        onChange={event => setDisplayName(event.target.value)}
                        className={INPUT_CLASS}
                        placeholder={`${selectedMember.last_name} ${selectedMember.first_name}`}
                      />
                      <span className="mt-1 block text-[11px] font-normal text-muted-foreground">
                        Одна строка целиком. Пусто — показывать паспортное ФИО.
                      </span>
                    </label>

                    <div className="rounded-md border border-border bg-background p-3 text-[11px] text-muted-foreground">
                      <div className="mb-1 font-medium text-foreground">Аккаунт приложения</div>
                      {identityLoading ? (
                        <span className="inline-flex items-center gap-2">
                          <Loader2 size={12} className="animate-spin" />
                          Ищу аккаунт
                        </span>
                      ) : !identityPreview || identityPreview.app_users.length === 0 ? (
                        <span>Аккаунт по совпадению ФИО не найден — меняем только форму имени.</span>
                      ) : (
                        <>
                          <select
                            value={appUserId ?? ''}
                            onChange={event => {
                              const value = event.target.value || null
                              setAppUserId(value)
                              const picked = identityPreview.app_users.find(u => u.user_id === value)
                              setAppFirstName(picked?.first_name ?? selectedMember.first_name)
                              setAppLastName(picked?.last_name ?? selectedMember.last_name)
                            }}
                            className={INPUT_CLASS}
                          >
                            <option value="">— не менять имя аккаунта —</option>
                            {identityPreview.app_users.map(user => (
                              <option key={user.user_id} value={user.user_id}>
                                {user.last_name} {user.first_name} · {user.email ?? 'без email'}
                                {user.match_kind === 'exact' ? '' : ' · только по фамилии'}
                              </option>
                            ))}
                          </select>
                          {appUserId && (
                            <div className="mt-2 grid grid-cols-2 gap-2">
                              <input
                                value={appFirstName}
                                onChange={event => setAppFirstName(event.target.value)}
                                className={INPUT_CLASS}
                                placeholder="Имя в аккаунте"
                              />
                              <input
                                value={appLastName}
                                onChange={event => setAppLastName(event.target.value)}
                                className={INPUT_CLASS}
                                placeholder="Фамилия в аккаунте"
                              />
                            </div>
                          )}
                          {appUserId && (
                            <label className="mt-2 flex items-start gap-2">
                              <input
                                type="checkbox"
                                checked={rememberAppUser}
                                onChange={event => setRememberAppUser(event.target.checked)}
                                className="mt-0.5"
                              />
                              <span>
                                Запомнить, что это аккаунт данной персоны. Тогда имя аккаунта дальше
                                будет обновляться само — и при смене формы имени, и при юридической
                                смене ФИО.
                              </span>
                            </label>
                          )}
                          <div className="mt-1">
                            У аккаунта имя и фамилия — отдельные поля, поэтому задаются здесь, а не одной
                            строкой. Пока аккаунт не связан с персоной, список — это кандидаты по совпадению
                            ФИО: если человек записан в приложении под другой фамилией, его тут не будет.
                          </div>
                        </>
                      )}
                    </div>

                    {identityPreview && (
                      <div className="rounded-md border border-blue-200 bg-blue-50 p-3 text-[11px] text-blue-900">
                        Форма имени разойдётся по спискам, ШМП и рейтингам. Исторических строк рейтинга —{' '}
                        {identityPreview.rating_rows.daily +
                          identityPreview.rating_rows.weekly +
                          identityPreview.rating_rows.period}
                        , они встанут в очередь пересчёта.
                      </div>
                    )}

                    <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-[11px] text-amber-800">
                      Паспортное ФИО ({selectedMember.last_name} {selectedMember.first_name}) здесь не меняется —
                      оно нужно в договорах и претензиях. Юридическая смена ФИО проводится юристами на карточке
                      ДКК в реестре документов.
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="rounded-md border border-border bg-background p-3 text-xs text-muted-foreground">
              <div className="mb-2 flex items-center gap-2 font-medium text-foreground">
                Состав группы
                {membersLoading && <Loader2 size={13} className="animate-spin text-muted-foreground" />}
              </div>
              {members.length === 0 ? (
                <div>Состав не найден.</div>
              ) : (
                <div className="max-h-36 space-y-1 overflow-y-auto pr-1">
                  {members.map(member => (
                    <div key={`${member.person_id}-${member.start_date}-${member.end_date ?? 'active'}`} className="flex justify-between gap-3">
                      <span className="min-w-0 truncate">{memberLabel(member)}</span>
                      <span className="shrink-0 text-right">
                        {formatDate(member.start_date)} - {member.end_date ? formatDate(member.end_date) : 'сейчас'}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="flex items-center justify-end gap-2 pt-1">
              <Button type="button" variant="outline" onClick={onClose} disabled={saving}>
                Закрыть
              </Button>
              <Button type="button" onClick={handleSubmit} disabled={!canSubmit}>
                {saving && <Loader2 size={15} className="mr-2 animate-spin" />}
                Провести
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}

